import { useRef, useState, type ReactNode, type PointerEvent as RPointerEvent, type MouseEvent as RMouseEvent } from "react";

const ACTION_W = 112;

/**
 * iOS-style swipe-to-delete row. Drag the content left (touch / mouse / pen via Pointer Events)
 * to reveal a delete action on the right; tap it to delete. Tapping the content while open closes
 * it. Keyboard users reach the delete button by Tab (which opens the row on focus).
 *
 * Drag only engages on clear horizontal intent (>8px, and more horizontal than vertical) so taps on
 * inner buttons and vertical page scroll keep working.
 */
export function SwipeRow({
  onDelete,
  disabled,
  deleting,
  children,
}: {
  onDelete: () => void;
  disabled?: boolean;
  deleting?: boolean;
  children: ReactNode;
}) {
  const [tx, setTx] = useState(0);
  const [open, setOpen] = useState(false);
  const txRef = useRef(0); // latest tx, read in onUp (state would be stale in the handler closure)
  const drag = useRef<{ x: number; y: number; active: boolean; base: number } | null>(null);
  const justDragged = useRef(false); // suppress the synthetic click that fires right after a swipe

  if (disabled) return <>{children}</>;

  const apply = (o: boolean) => {
    txRef.current = o ? -ACTION_W : 0;
    setOpen(o);
    setTx(txRef.current);
  };

  function onDown(e: RPointerEvent) {
    drag.current = { x: e.clientX, y: e.clientY, active: false, base: open ? -ACTION_W : 0 };
  }
  function onMove(e: RPointerEvent) {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.active) {
      if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy) + 2) {
        d.active = true;
        try {
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      } else if (Math.abs(dy) > 10) {
        drag.current = null; // vertical scroll — let it through
        return;
      } else {
        return;
      }
    }
    e.preventDefault();
    const next = Math.max(-ACTION_W - 28, Math.min(0, d.base + dx));
    txRef.current = next;
    setTx(next);
  }
  function onUp() {
    const d = drag.current;
    drag.current = null;
    if (!d || !d.active) return;
    // A drag just happened — the browser will fire a click next; swallow it so it neither triggers
    // an inner button nor (when we just opened) immediately closes the row. Auto-clear in case no
    // click follows, so a later genuine tap still works.
    justDragged.current = true;
    setTimeout(() => {
      justDragged.current = false;
    }, 350);
    apply(txRef.current < -ACTION_W / 2);
  }
  function onClickCapture(e: RMouseEvent) {
    // Consume the gesture's own click (don't close, don't trigger inner actions).
    if (justDragged.current) {
      justDragged.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // A genuine separate tap while open closes the row.
    if (open) {
      e.preventDefault();
      e.stopPropagation();
      apply(false);
    }
  }

  return (
    <div style={{ position: "relative", overflow: "hidden", touchAction: "pan-y" }}>
      <button
        type="button"
        aria-label="この手順を削除"
        onClick={onDelete}
        onFocus={() => apply(true)}
        disabled={deleting}
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: ACTION_W,
          border: 0,
          cursor: "pointer",
          background: "#e5484d",
          color: "#fff",
          fontWeight: 700,
          fontSize: 13,
          letterSpacing: ".06em",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M4 7h16M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7M6.5 7l.8 11a2 2 0 0 0 2 1.9h5.4a2 2 0 0 0 2-1.9l.8-11" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {deleting ? "削除中…" : "削除"}
      </button>
      <div
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onClickCapture={onClickCapture}
        style={{
          position: "relative",
          background: "var(--surface)",
          transform: `translateX(${tx}px)`,
          transition: drag.current?.active ? "none" : "transform .22s cubic-bezier(.2,.8,.2,1)",
          willChange: "transform",
        }}
      >
        {children}
      </div>
    </div>
  );
}
